import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { loadLogoForPDF, addLogoToPDF } from '../pdfLogoUtil';

const fmt = (val) =>
  val != null
    ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : 'N/A';

const IncomeByMonthPDF = ({ chartData, chartRef, user }) => {
  const [generating, setGenerating] = useState(false);

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const pdf = new jsPDF('l', 'mm', 'letter');
      const userName = user?.name || 'Unknown User';
      const timestamp = new Date().toLocaleString();
      const logoDataUrl = await loadLogoForPDF();
      const addLogo = (x, yPos, w, h) => addLogoToPDF(pdf, logoDataUrl, x, yPos, w, h);

      const {
        chartItems, forecastItems, forecastStats,
        totalRevenue, avgMonthly, variancePct,
        highestMonth, lowestMonth, stabilityRating, monthCount,
      } = chartData;

      // ============================================================
      // PAGE 1: Chart (landscape)
      // ============================================================
      const lPageWidth = pdf.internal.pageSize.getWidth();
      const lPageHeight = pdf.internal.pageSize.getHeight();
      const lMargin = 15;
      let y = lMargin;

      const headerLogoH = 16;
      const headerLogoW = headerLogoH * 3;
      addLogo(lPageWidth - lMargin - headerLogoW, y, headerLogoW, headerLogoH);

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Income by Month', lMargin, y + 6);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, lMargin, y + 12);
      y += headerLogoH + 4;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(lMargin, y, lPageWidth - lMargin, y);
      y += 3;

      if (chartRef?.current) {
        try {
          const canvas = await html2canvas(chartRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            useCORS: true,
          });
          const chartImgData = canvas.toDataURL('image/png');
          const footerReserve = 32;
          const availableHeight = lPageHeight - y - footerReserve;
          const availableWidth = lPageWidth - lMargin * 2;
          const aspectRatio = canvas.height / canvas.width;
          let imgWidth = availableWidth;
          let imgHeight = imgWidth * aspectRatio;
          if (imgHeight > availableHeight) {
            imgHeight = availableHeight;
            imgWidth = imgHeight / aspectRatio;
          }
          const imgX = lMargin + (availableWidth - imgWidth) / 2;
          pdf.addImage(chartImgData, 'PNG', imgX, y, imgWidth, imgHeight);
        } catch (e) {
          console.error('Chart capture failed:', e);
        }
      }

      // ============================================================
      // PAGE 2+: Analytics (portrait)
      // ============================================================
      pdf.addPage('letter', 'portrait');
      const aPageWidth = pdf.internal.pageSize.getWidth();
      const aPageHeight = pdf.internal.pageSize.getHeight();
      const aMargin = 20;
      y = aMargin;

      const aLogoH = 14;
      const aLogoW = aLogoH * 3;
      addLogo(aPageWidth - aMargin - aLogoW, y, aLogoW, aLogoH);

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 58, 138);
      pdf.text('Income by Month', aMargin, y + 6);
      pdf.text('Revenue Analysis Report', aMargin, y + 14);
      y += 20;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Prepared by: ${userName}  |  Generated: ${timestamp}`, aMargin, y);
      y += 5;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);
      pdf.line(aMargin, y, aPageWidth - aMargin, y);
      y += 8;

      const checkPageBreak = (neededSpace) => {
        if (y + neededSpace > aPageHeight - 35) {
          pdf.addPage('letter', 'portrait');
          y = aMargin;
        }
      };

      const sectionHeader = (title) => {
        checkPageBreak(20);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 58, 138);
        pdf.text(title, aMargin, y);
        y += 9;
      };

      const bulletLine = (text, indent = 4) => {
        checkPageBreak(7);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(40, 40, 40);
        const lines = pdf.splitTextToSize(text, aPageWidth - aMargin * 2 - indent - 4);
        pdf.text(`•  ${lines[0]}`, aMargin + indent, y);
        y += 5;
        for (let i = 1; i < lines.length; i++) {
          checkPageBreak(5);
          pdf.text(`   ${lines[i]}`, aMargin + indent, y);
          y += 5;
        }
      };

      // Revenue Overview
      sectionHeader('Revenue Overview');
      bulletLine(`Months tracked: ${monthCount} (${chartItems[0].label} — ${chartItems[chartItems.length - 1].label})`);
      bulletLine(`Total revenue: ${fmt(totalRevenue)}`);
      bulletLine(`Average monthly revenue: ${fmt(avgMonthly)}`);
      bulletLine(`Revenue stability: ${stabilityRating} (${variancePct.toFixed(1)}% month-to-month variance)`);
      bulletLine(`Peak month: ${highestMonth.label} — ${fmt(highestMonth.income)}`);
      bulletLine(`Lowest month: ${lowestMonth.label} — ${fmt(lowestMonth.income)}`);
      bulletLine(`Revenue range: ${fmt(highestMonth.income - lowestMonth.income)}`);
      y += 4;

      // Monthly Breakdown
      sectionHeader('Monthly Revenue Breakdown');
      chartItems.forEach(item => {
        const vsAvg = avgMonthly > 0
          ? (((item.income - avgMonthly) / avgMonthly) * 100).toFixed(1)
          : '0.0';
        const sign = item.income >= avgMonthly ? '+' : '';
        bulletLine(`${item.label}: ${fmt(item.income)}   (${sign}${vsAvg}% vs avg)`);
      });
      y += 4;

      // Forecast
      if (forecastStats && forecastItems.length > 0) {
        sectionHeader('Revenue Forecast (6-Month)');
        bulletLine(`Trend direction: ${forecastStats.trendDirection.charAt(0).toUpperCase() + forecastStats.trendDirection.slice(1)} (${forecastStats.trendPct.toFixed(1)}% projected change)`);
        bulletLine(`Forecast period: ${forecastStats.forecastPeriod}`);
        bulletLine(`Projected average: ${fmt(forecastStats.projectedAvg)}/month`);
        bulletLine(`Forecast confidence (R²): ${forecastStats.confidence}%`);
        y += 2;
        forecastItems.forEach(item => {
          bulletLine(`${item.label}: ${fmt(item.income)} (projected)`, 8);
        });
        y += 4;
      }

      // Practice Intelligence
      sectionHeader('Practice Intelligence');
      const revenueGap = highestMonth.income - avgMonthly;
      bulletLine(`Revenue gap to peak: ${fmt(revenueGap)}/month — ${fmt(revenueGap * 12)}/year opportunity if peak performance sustained`);
      bulletLine(`Low-month recovery opportunity: ${fmt(avgMonthly - lowestMonth.income)} — raising ${lowestMonth.label} to the monthly average`);
      if (forecastStats) {
        const direction = forecastStats.projectedAvg > avgMonthly ? 'above' : 'below';
        bulletLine(`Forecast vs historical: Projected average ${fmt(forecastStats.projectedAvg)} is ${fmt(Math.abs(forecastStats.projectedAvg - avgMonthly))} ${direction} historical average`);
      }
      y += 4;

      // Disclaimer
      checkPageBreak(25);
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(180, 130, 0);
      const disclaimerText =
        'Practice Management Notice: This report is generated from self-reported revenue data for practice planning purposes only. For financial decisions, consult a qualified financial or accounting professional.';
      const disclaimerLines = pdf.splitTextToSize(disclaimerText, aPageWidth - aMargin * 2);
      disclaimerLines.forEach(line => {
        checkPageBreak(6);
        pdf.text(line, aMargin, y);
        y += 5;
      });

      pdf.save(`Income_by_Month_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('PDF generation failed: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const hasData = chartData && chartData.chartItems && chartData.chartItems.length > 0;

  return (
    <button
      onClick={generatePDF}
      disabled={generating || !hasData}
      className={`px-6 py-3 rounded-lg font-semibold transition-all ${
        generating
          ? 'bg-gray-300 text-gray-500 cursor-wait'
          : !hasData
          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
          : 'bg-green-600 text-white hover:bg-green-700 shadow-md'
      }`}
    >
      {generating ? 'Generating PDF...' : 'Download PDF'}
    </button>
  );
};

export default IncomeByMonthPDF;
